import { CALL_API } from "redux-api-middleware";
import responseHandler from "responseHandler";
import WebConfig from 'middlewares/WebConfig';
import store from "../../index";
import { addRequest, removeRequest, environmentAwareConsoleLog } from "../requestTracker";
import trackedRequests from "../requestTracker/trackedRequests";

const HTTP_GET = "GET";
const HTTP_POST = "POST";
const HTTP_PUT = "PUT";
const HTTP_DELETE = "DELETE";

const emptyRequest = {
  [CALL_API]: {
    endpoint: "",
    method: "",
    body: null,
    types: [
      "NONE"
    ]
  }
};

const _WebApiReduxRequest = (REQ_ACTION_TYPE, RES_ACTION_TYPE, ERR_ACTION_TYPE, method, endpoint, callback = null, payload = null, errorCallback = null) => {
  const key = addRequest(method, endpoint, JSON.stringify(payload), callback, errorCallback, null, null);
  if (!key) return emptyRequest;

  // tracking to make sure we call the requests that were made with mapDispatchToProps. Making sure the request was actually dispatched
  let timer = window.setTimeout(() => {
    if (environmentAwareConsoleLog("Redux function was called but was not dispatched - this will do nothing and possibly break further calls to this endpoint", {REQ_ACTION_TYPE, RES_ACTION_TYPE, ERR_ACTION_TYPE, method, endpoint})) {
      // eslint-disable-next-line no-console
      console.error("Trace below is for the redux error from above, please resolve (expand this line in your browser console for the trace of what might be calling redux without dispatching it - did you accidentally call the redux function directly and not props.[function]?)");
    }
  }, 2000);

  return dispatch => {
    window.clearTimeout(timer);
    dispatch({
      [CALL_API]: {
        endpoint: endpoint,
        method: method,
        body: payload !== null ? JSON.stringify(payload) : null,
        types: [
          REQ_ACTION_TYPE,
          {
            type: RES_ACTION_TYPE,
            payload: (action, state, res) =>
              res.json().then(data => {
                if (trackedRequests && trackedRequests[key]) {
                  trackedRequests[key].forEach(callBack => {
                    callBack.success && dispatch(callBack.success());
                  });
                }
                removeRequest(key);
                return data;
              })
          },
          {
            type: ERR_ACTION_TYPE,
            payload: (action, state, res) => {
              trackedRequests[key].forEach(callBack => {
                callBack.error && dispatch(callBack.error());
              });
              removeRequest(key);
              responseHandler.error(res, dispatch);
            }
          }
        ]
      }
    });
  };
};

const WebApiReduxRequest = (REQ_ACTION_TYPE, RES_ACTION_TYPE, ERR_ACTION_TYPE, method, endpoint, payload = null) =>
  _WebApiReduxRequest(REQ_ACTION_TYPE, RES_ACTION_TYPE, ERR_ACTION_TYPE, method, endpoint, null, payload);

const WebApiReduxRequestWithCallback = (REQ_ACTION_TYPE, RES_ACTION_TYPE, ERR_ACTION_TYPE, method, endpoint, callback, payload = null, errorCallback = null) =>
  _WebApiReduxRequest(REQ_ACTION_TYPE, RES_ACTION_TYPE, ERR_ACTION_TYPE, method, endpoint, callback, payload, errorCallback);

//https://github.com/github/fetch/issues/155#issuecomment-108288863
const mFetch = async (url, options = {}) => {
  if (options.credentials == null) options.credentials = 'same-origin';
  let resp = fetch(url, options);
  return resp.then(response => {
    if (response.status >= 200 && response.status < 300) {
      return Promise.resolve(response);
    }
    return Promise.reject(response);
  }).catch(response => {
    const error = new Error(response.statusText || response.status);
    error.response = response;
    window.console.log(error.response); //log the error here for ease of debugging
    return Promise.reject(error);
  });
};

const WebApiRequest = async ({ method, endpoint, payload = null, successCallback = null, errorCallback = null, isFetchingCallback = null, mountedRef = null, options = {}, file = null, arrayBuffer = null, arrayBufferContentType = null, disableDefaultErrorResponseHandler = false, disableDefaultSuccessResponseHandler = false }) => {
  const { headers = {}, ...otherOptions } = options;

  const key = addRequest(method, endpoint, JSON.stringify(payload), successCallback, errorCallback, isFetchingCallback, mountedRef);
  if (!key) return emptyRequest;

  let isJson = true;
  let requestPayload = payload;
  if (file !== null) {
    requestPayload = new FormData();
    requestPayload.append('file', file);
    delete headers['Content-Type'];
    isJson = false;
  }
  else if (arrayBuffer !== null) {
    requestPayload = arrayBuffer;
    if (arrayBufferContentType) {
      headers["Content-Type"] = arrayBufferContentType;
    } else {
      headers["Content-Type"] = "application/octet-stream";
    }

    isJson = false;
  }
  else if (payload !== null) {
    headers["Content-Type"] = "application/json";
  }

  let webRequestUrl = endpoint;
  if (webRequestUrl.indexOf("http") !== 0) {
    webRequestUrl = WebConfig.webApiRoot + webRequestUrl;
  }

  await mFetch(webRequestUrl, {
    method: method,
    ...requestPayload && { body: isJson ? JSON.stringify(requestPayload) : requestPayload },
    headers: {
      ...store ? { Authorization: `Bearer ${store.getState().user.profile.token}` } : {},
      ...headers["Content-Type"] === undefined && file !== null
        ? {}
        : headers["Content-Type"] === undefined
          ? { "Content-Type": "application/json" }
          : headers["Content-Type"] === null
            ? {}
            : { "Content-Type": headers["Content-Type"] }
    },
    ...otherOptions,
  })
    .then(resp =>
      resp.json().then(data => {
        (!disableDefaultSuccessResponseHandler) && data && data.message && responseHandler.success(data.message, store.dispatch);
        if (trackedRequests && trackedRequests[key]) {
          trackedRequests[key].forEach(callBack => {
            if (callBack.mounted && callBack.mounted.current) {
              callBack.success && callBack.success(data);
              callBack.fetching && callBack.fetching(false);
            } else {
              environmentAwareConsoleLog(`Did not call success for key ${key} because the component is no longer mounted`);
            }
          });
        }
      }).catch((error) => {
        if (!disableDefaultErrorResponseHandler)
          responseHandler.error(error.response, store.dispatch);
        if (trackedRequests && trackedRequests[key]) {
          trackedRequests[key].forEach(callBack => {
            if (callBack.mounted && callBack.mounted.current) {
              callBack.error && callBack.error(error);
              callBack.fetching && callBack.fetching(false);
            } else {
              environmentAwareConsoleLog(`Did not call error for key ${key} because the component is no longer mounted`);
            }
          });
        }
      }))
    .catch((error) => {
      if (!disableDefaultErrorResponseHandler)
        responseHandler.error(error.response, store.dispatch);
      if (trackedRequests && trackedRequests[key]) {
        trackedRequests[key].forEach(callBack => {
          if (callBack.mounted && callBack.mounted.current) {
            callBack.error && callBack.error(error);
            callBack.fetching && callBack.fetching(false);
          } else {
            environmentAwareConsoleLog(`Did not call error for key ${key} because the component is no longer mounted`);
          }
        });
      }
    }).finally(() => {
      removeRequest(key);
    });
};

const VersionRequest = async () => {
  const response = await mFetch(WebConfig.versionURL);
  return await response;
};


const WebRequest = ({ uri, method, successCallback = null, errorCallback = null, payload = null, responseJson = true }) => {
  fetch(uri, {
    method: method,
    ...payload && { body: JSON.stringify(payload) },
  })
    .then(response => responseJson ? response.json() : response.text())
    .then(data => { if (successCallback) successCallback(data); })
    .catch(error => { if (errorCallback) errorCallback(error); });
};

export {
  HTTP_GET,
  HTTP_POST,
  HTTP_PUT,
  HTTP_DELETE,
  WebApiReduxRequest,
  WebApiReduxRequestWithCallback,
  WebApiRequest,
  WebRequest,
  VersionRequest,
};
